import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import axios from 'axios';

// Importa tus componentes de Soft UI (ajusta la ruta según tu estructura)
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftSelect from 'components/SoftSelect';

// Importa SweetAlert2
import Swal from 'sweetalert2';

const AddRowModal = ({ show, handleClose, handleSubmit, consolidatedNumber, agent }) => {
  // Nota: se recibe `agent` por props si así lo requieres.
  const [airWaybill, setAirWaybill] = useState('');
  const [charge, setCharge] = useState('');
  const [costPP, setCostPP] = useState('');
  const [costCC, setCostCC] = useState('');
  const [incomePP, setIncomePP] = useState('');
  const [incomeCC, setIncomeCC] = useState('');

  // Estados para las opciones de SoftSelect
  const [airwaybillOptions, setAirwaybillOptions] = useState([]);
  const [chargeOptions, setChargeOptions] = useState([]);

  // Opción por defecto para cada select
  const defaultAirwaybillOption = { value: '', label: 'Seleccione Air Waybill' };
  const defaultChargeOption = { value: '', label: 'Seleccione Charge' };

  // Cargar las guías asociadas al consolidado
  useEffect(() => {
    if (consolidatedNumber && show) {
      axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/finance/airwaybill-list/${consolidatedNumber}/`)
        .then((response) => {
          const options = response.data.map((item) => ({
            value: item.waybill,
            label: item.waybill,
          }));
          setAirwaybillOptions(options);
        })
        .catch((error) => {
          console.error('Error al obtener las guías asociadas:', error);
        });
    }
  }, [consolidatedNumber, show]);

  // Cargar las opciones de Charge desde el endpoint
  useEffect(() => {
    if (show) {
      axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/finance/charge-options/`)
        .then((response) => {
          const options = response.data.map((item) => ({
            value: item.charge,
            label: item.charge,
          }));
          setChargeOptions(options);
        })
        .catch((error) => {
          console.error('Error al obtener las opciones de Charge:', error);
        });
    }
  }, [show]);

  const showToast = (icon, title) => {
    Swal.fire({
      icon,
      title,
      toast: true,
      position: 'top-end',
      timer: 3000,
      showConfirmButton: false,
    });
  };

  const onSubmit = () => {
    // Validaciones básicas
    if (!airWaybill) {
      showToast('warning', 'Debes seleccionar un Air Waybill!');
      return;
    }
    if (!charge) {
      showToast('warning', 'Debes seleccionar un Charge!');
      return;
    }
    if (isNaN(costPP) || isNaN(costCC) || isNaN(incomePP) || isNaN(incomeCC)) {
      showToast('warning', 'Los campos Cost y Income deben ser numéricos!');
      return;
    }
    if (!costPP && !costCC && !incomePP && !incomeCC) {
      showToast('warning', 'Debes ingresar al menos un Cost o Income!');
      return;
    }

    // Envía la petición POST al endpoint de Django
    axios
      .post(`${process.env.REACT_APP_SERVER_URL}/api/finance/consolidated/charges/`, {
        con: consolidatedNumber,
        way: airWaybill,
        cha: charge,
        costpp1: costPP,
        costcc1: costCC,
        incomepp1: incomePP,
        incomecc1: incomeCC,
        agente: agent, // Se envía el agente recibido vía prop (si lo usas)
      })
      .then((response) => {
        // Muestra el toast de éxito
        showToast('success', response.data.message);

        // Llama a handleSubmit (que en el padre refresca la tabla)
        handleSubmit();

        // Reinicia los estados
        setAirWaybill('');
        setCharge('');
        setCostPP('');
        setCostCC('');
        setIncomePP('');
        setIncomeCC('');

        // Cierra el modal
        handleClose();
      })
      .catch((error) => {
        console.error('Error al guardar el item:', error);
        // Muestra el toast de error
        showToast('error', 'Error al guardar el item, intente nuevamente.');
      });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="md"
      centered
      backdrop="static"
      style={{ zIndex: 1100 }}
    >
      <Modal.Header closeButton>
        <SoftTypography variant="h5" fontWeight="bold">
          Agregar Nueva Fila
        </SoftTypography>
      </Modal.Header>
      <Modal.Body>
        <SoftBox component="form" display="flex" flexDirection="column" gap={2}>
          <SoftTypography variant="caption">Air Waybill</SoftTypography>
          <SoftSelect
            options={[defaultAirwaybillOption, ...airwaybillOptions]}
            value={
              airwaybillOptions.find((option) => option.value === airWaybill) ||
              defaultAirwaybillOption
            }
            onChange={(selectedOption) => setAirWaybill(selectedOption.value)}
          />

          <SoftTypography variant="caption">Charge</SoftTypography>
          <SoftSelect
            options={[defaultChargeOption, ...chargeOptions]}
            value={
              chargeOptions.find((option) => option.value === charge) ||
              defaultChargeOption
            }
            onChange={(selectedOption) => setCharge(selectedOption.value)}
          />

          <SoftTypography variant="caption">Cost PP</SoftTypography>
          <SoftInput
            type="number"
            value={costPP}
            onChange={(e) => setCostPP(e.target.value)}
            placeholder="Ingrese Cost PP"
          />

          <SoftTypography variant="caption">Cost CC</SoftTypography>
          <SoftInput
            type="number"
            value={costCC}
            onChange={(e) => setCostCC(e.target.value)}
            placeholder="Ingrese Cost CC"
          />

          <SoftTypography variant="caption">Income PP</SoftTypography>
          <SoftInput
            type="number"
            value={incomePP}
            onChange={(e) => setIncomePP(e.target.value)}
            placeholder="Ingrese Income PP"
          />

          <SoftTypography variant="caption">Income CC</SoftTypography>
          <SoftInput
            type="number"
            value={incomeCC}
            onChange={(e) => setIncomeCC(e.target.value)}
            placeholder="Ingrese Income CC"
          />
        </SoftBox>
      </Modal.Body>
      <Modal.Footer>
        <SoftButton color="success" variant="gradient" onClick={onSubmit}>
          Guardar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
};

AddRowModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired, // Se espera que refresque la tabla en el padre
  consolidatedNumber: PropTypes.string.isRequired,
  agent: PropTypes.string, // Sólo si lo necesitas
};

AddRowModal.defaultProps = {
  agent: '',
};

export default AddRowModal;
